/**
 * Iter-52.55.8 — In-app browser detection.
 *
 * MSG91's OTP widget (otp-provider.js) fails to initialise reliably
 * inside WhatsApp / Facebook / Instagram in-app browsers because
 * they either delay third-party script execution, sandbox it, or
 * refuse to expose the globals the widget attaches. Since >70% of
 * our leads come from WhatsApp/FB/IG ads, we detect the browser
 * class so we can (a) show a friendly "Open in Chrome / Safari"
 * hint and (b) tag the widget-failure telemetry with the exact UA
 * bucket so the admin funnel shows how many customers are being
 * blocked in each webview.
 */

const UA = typeof navigator !== 'undefined' ? navigator.userAgent : '';

// Ordered — the more specific webviews first (WhatsApp UAs contain
// "; wv" too, so bucket-them before the generic Android WebView check).
const IN_APP_PATTERNS = [
  { key: 'whatsapp_webview',  test: /WhatsApp\//i },
  { key: 'facebook_webview',  test: /(FBAN|FBAV|FB_IAB|FBIOS)/i },
  { key: 'instagram_webview', test: /Instagram/i },
];

const OTHER_IN_APP_PATTERNS = [
  /Line\//i, /Twitter/i, /MicroMessenger/i, /Snapchat/i,
  /LinkedInApp/i, /Pinterest/i, /Threads/i, /TelegramWebview/i,
  // Generic Android WebView marker. Only match when we haven't
  // already classified into a named webview.
  /Version\/[\d.]+ Chrome\/[\d.]+ Mobile Safari.*wv/i,
  /; wv\)/i,
];

/**
 * Return one of the known UA class strings.  Server-side enum kept in
 * sync with backend `_SIGNUP_UA_CLASSES`:
 *   whatsapp_webview | facebook_webview | instagram_webview |
 *   other_inapp | chrome_android | safari_ios | chrome_desktop |
 *   firefox | other
 */
export function classifyUserAgent(ua = UA) {
  if (!ua) return 'other';
  for (const p of IN_APP_PATTERNS) if (p.test.test(ua)) return p.key;
  for (const p of OTHER_IN_APP_PATTERNS) if (p.test(ua)) return 'other_inapp';
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);
  const isFirefox = /Firefox\//i.test(ua);
  const isChrome = /Chrome\//i.test(ua) && !/Edg\//i.test(ua);
  if (isIOS && !isChrome && /Safari/i.test(ua)) return 'safari_ios';
  if (isAndroid && isChrome) return 'chrome_android';
  if (isFirefox) return 'firefox';
  if (isChrome) return 'chrome_desktop';
  if (/Safari/i.test(ua) && !isAndroid) return 'safari_ios';
  return 'other';
}

/** True when the class indicates a webview that historically blocks
 *  MSG91's SDK — used to show the "open in a proper browser" hint. */
export function isInAppBrowser(uaClass = classifyUserAgent()) {
  return uaClass === 'whatsapp_webview'
    || uaClass === 'facebook_webview'
    || uaClass === 'instagram_webview'
    || uaClass === 'other_inapp';
}

/** Return an Android intent-URL that opens the current page in Chrome.
 *  Falls back to the same URL if the intent can't launch (Android's
 *  behaviour when Chrome isn't installed).  iOS has no equivalent
 *  intent — the parent component shows a copy-paste instruction. */
export function openInChromeUrl() {
  if (typeof window === 'undefined') return '';
  const rest = window.location.href.replace(/^https?:\/\//, '');
  return `intent://${rest}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(window.location.href)};end`;
}

/** True on iPhone/iPad. iOS instructions are different from Android
 *  because iOS webviews can't be redirected to Safari via an intent. */
export function isIOS() {
  return /iPhone|iPad|iPod/i.test(UA);
}
